import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
    LayoutDashboard, Briefcase, IdCard, FileText, MessageSquare, Bell, Settings,
    Users, Sparkles, HelpCircle, Search, BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/* Universal Cmd+K quick-nav.
 * Search across pages, then power-tools per role. */

const PUBLIC_ITEMS = [
    { label: "Home", to: "/", icon: LayoutDashboard, group: "Navigate" },
    { label: "Platform overview", to: "/platform", icon: Sparkles, group: "Navigate" },
    { label: "For Employers", to: "/employers", icon: Briefcase, group: "Navigate" },
    { label: "For Talent", to: "/talent", icon: IdCard, group: "Navigate" },
    { label: "Open Roles", to: "/careers", icon: Briefcase, group: "Navigate" },
    { label: "Pricing", to: "/pricing", icon: FileText, group: "Navigate" },
    { label: "Resources · Blog", to: "/resources#blog", icon: BookOpen, group: "Resources" },
    { label: "Resources · Help Center", to: "/resources#help", icon: HelpCircle, group: "Resources" },
    { label: "Resources · FAQs", to: "/resources#faqs", icon: HelpCircle, group: "Resources" },
    { label: "Resources · Career", to: "/resources#career", icon: BookOpen, group: "Resources" },
    { label: "Resources · Hiring", to: "/resources#hiring", icon: BookOpen, group: "Resources" },
    { label: "Contact", to: "/contact", icon: MessageSquare, group: "Navigate" },
];

const USER_ITEMS = [
    { label: "My Dashboard", to: "/dashboard", icon: LayoutDashboard, group: "Me" },
    { label: "Inbox / Messages", to: "/inbox", icon: MessageSquare, group: "Me" },
    { label: "Notifications", to: "/notifications", icon: Bell, group: "Me" },
    { label: "Settings", to: "/settings", icon: Settings, group: "Me" },
];

const PRO_ITEMS = [
    { label: "Talent Passport", to: "/talent", icon: IdCard, group: "Talent" },
    { label: "Job Matches", to: "/professional/matches", icon: Briefcase, group: "Talent" },
    { label: "Application Tracker", to: "/professional/applications", icon: FileText, group: "Talent" },
];

const EMP_ITEMS = [
    { label: "Employer Dashboard", to: "/employer/dashboard", icon: LayoutDashboard, group: "Employer" },
    { label: "Post a Job", to: "/employer/dashboard#post-job", icon: Briefcase, group: "Employer" },
    { label: "Candidate Pipeline", to: "/employer/pipeline", icon: Users, group: "Employer" },
];

export const CommandPalette = () => {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    let items = [...PUBLIC_ITEMS];
    if (user) {
        items = [...USER_ITEMS, ...items];
        if (user.role === "professional") items = [...PRO_ITEMS, ...items];
        if (user.role === "employer") items = [...EMP_ITEMS, ...items];
    }

    // Group by "group"
    const groups = items.reduce((acc, it) => {
        (acc[it.group] = acc[it.group] || []).push(it);
        return acc;
    }, {});

    const go = (to) => {
        setOpen(false);
        // support hash + query
        if (to.startsWith("#") || to.includes("?")) {
            window.location.assign(to);
        } else {
            navigate(to);
        }
    };

    return (
        <>
            <button
                data-testid="command-palette-trigger"
                onClick={() => setOpen(true)}
                className="hidden md:inline-flex items-center gap-2 h-9 pl-3 pr-8 rounded-full border border-border bg-card text-xs text-muted-foreground hover:bg-muted transition-colors relative"
                aria-label="Open command palette"
            >
                <Search size={13} /> Quick search
                <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-muted border border-border">⌘K</kbd>
            </button>
            <CommandDialog open={open} onOpenChange={setOpen}>
                <CommandInput placeholder="Search pages, sections, actions…" data-testid="command-palette-input" />
                <CommandList>
                    <CommandEmpty>No matches.</CommandEmpty>
                    {Object.entries(groups).map(([group, entries]) => (
                        <CommandGroup key={group} heading={group}>
                            {entries.map((it) => (
                                <CommandItem
                                    key={it.to}
                                    value={`${it.group} ${it.label}`}
                                    onSelect={() => go(it.to)}
                                    data-testid={`cmdk-item-${it.to}`}
                                >
                                    <it.icon size={14} className="mr-2 text-brand-green" /> {it.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    ))}
                </CommandList>
            </CommandDialog>
        </>
    );
};

export default CommandPalette;
